<script setup lang="ts">
import { toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import {
    CannotCreateEligibilitySubmission,
    CannotGetQuestions,
    HasAnAdvisor,
    PersonalDetailsMissing,
    PostalCodeMissing,
} from '../Errors'
import { Action, Actor, enrollmentTracking } from '../Integrations/Tracking'
import { installGtm } from '../Integrations/gtm'
import { useConfigStore } from '../Stores/config'
import { useEligibilitySubmissionStore } from '../Stores/eligibilitySubmission'
import { useQuestionStore } from '../Stores/question'
import { Locale } from '../global'
import { marketingSourceFromQuery, toAtomString } from '../utils'

const props = defineProps<{
    uuid: string
}>()

const { locale } = useI18n()
const language: Locale = toValue(locale) as Locale

const { query } = useRoute()
const router = useRouter()

const configStore = useConfigStore()
const eligibilitySubmissionStore = useEligibilitySubmissionStore()
const questionStore = useQuestionStore()

try {
    await eligibilitySubmissionStore.createFromCampaign(props.uuid, {
        externalId: query.externalId?.toString(),
        firstName: query.firstName?.toString(),
        lastName: query.lastName?.toString(),
        companyName: query.companyName?.toString(),
        vatNumber: query.vatNumber?.toString(),
        emailAddress: encodeURIComponent(query.emailAddress?.toString() ?? '')
            .replace(/%20/g, '+')
            .replace(/%40/g, '@'),
        phoneNumber: query.phoneNumber?.toString(),
        street: query['address[street]']?.toString(),
        number: query['address[number]']?.toString(),
        extension: query['address[extension]']?.toString(),
        city: query['address[city]']?.toString(),
        postalCode: query['address[postalCode]']?.toString(),
        privacyPolicyDisplayedAt: toAtomString(new Date()),
        language,
        ...marketingSourceFromQuery(query),
    })

    if (eligibilitySubmissionStore.error) {
        throw new CannotCreateEligibilitySubmission('Error creating eligibility submission')
    }

    installGtm(
        eligibilitySubmissionStore.eligibilitySubmission.campaign.advertising.googleTagManagerId
    )

    if (eligibilitySubmissionStore.eligibilitySubmission.advisors.setle) {
        if (
            eligibilitySubmissionStore.campaignSwapPIScreen &&
            !eligibilitySubmissionStore.eligibilitySubmission.address?.postalCode
        ) {
            throw new PostalCodeMissing('Postal code missing')
        }

        if (
            !eligibilitySubmissionStore.campaignSwapPIScreen &&
            eligibilitySubmissionStore.isMissingPersonalDetails
        ) {
            throw new PersonalDetailsMissing('Personal details missing')
        }

        throw new HasAnAdvisor(
            'Has Setle advisor',
            eligibilitySubmissionStore.eligibilitySubmission.advisors.setle.url
        )
    }

    if (
        eligibilitySubmissionStore.campaignSwapPIScreen &&
        !eligibilitySubmissionStore.eligibilitySubmission.address?.postalCode
    ) {
        throw new PostalCodeMissing('Postal code missing')
    } else {
        await questionStore.getQuestions(props.uuid)

        if (questionStore.error) {
            throw new CannotGetQuestions('Error getting questions')
        }
    }

    router.push({ name: 'home', query: { campaignId: props.uuid, ...query }, replace: true })
} catch (error) {
    if (error instanceof CannotCreateEligibilitySubmission || error instanceof CannotGetQuestions) {
        router.push({ name: 'error' })
    } else if (error instanceof PersonalDetailsMissing) {
        configStore.$patch({ shouldDisplayPersonalDetails: true })
        router.push({
            name: 'personal-details',
            query: { ...query, showNavigationHeader: 'false' },
        })
    } else if (error instanceof PostalCodeMissing) {
        configStore.$patch({ shouldDisplayPostalCode: true })
        router.push({
            name: 'postal-code',
            query: { ...query, campaignId: props.uuid },
            replace: true,
        })
    } else if (error instanceof HasAnAdvisor) {
        if (error.advisorUrl) {
            enrollmentTracking.track(Actor.Homeowner, Action.RedirectToSetle)
            window.location.assign(error.advisorUrl)
        }
    }
}
</script>

<template>
    <div />
</template>
